/*
  Snel bestellen: het hele assortiment in één tabel, op artikelnummer.

  De producten komen uit sections/quick-order-data.liquid, opgehaald via de
  Section Rendering API op /collections/all, 250 per keer. Dat gebeurt in de
  context van de ingelogde klant, dus met zijn B2B-prijzen. Daarna doet dit
  script de rest in de browser:

  - sorteren op artikelnummer, met getallen als getal (2L677 voor 2L1043);
  - tabjes (Alles, Bestsellers, Armbanden...) op de collecties van een product;
  - zoeken op naam, kleur en artikelnummer, over alles;
  - pagina's van 50 regels.

  Aantallen staan in één lijst die blijft bestaan als je van tabje of pagina
  wisselt. Pas bij "Alles toevoegen" worden ze als verborgen velden in het
  formulier gezet; de lade in de header onderschept het formulier, zet alles
  in de winkelwagen en klapt open.
*/
const QUICK_COLLATOR = new Intl.Collator('nl', { numeric: true, sensitivity: 'base' });

class QuickOrder {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('[data-quick]');
    if (!this.form) return;

    try {
      this.config = JSON.parse(this.form.querySelector('[data-quick-config]').textContent);
    } catch (error) {
      return;
    }

    this.body = this.form.querySelector('[data-quick-body]');
    this.status = this.form.querySelector('[data-quick-status]');
    this.none = this.form.querySelector('[data-quick-none]');
    this.pager = this.form.querySelector('[data-quick-pager]');
    this.pageInfo = this.form.querySelector('[data-quick-page]');
    this.prev = this.form.querySelector('[data-quick-prev]');
    this.next = this.form.querySelector('[data-quick-next]');
    this.hidden = this.form.querySelector('[data-quick-items]');
    this.totalEl = this.form.querySelector('[data-quick-total]');
    this.submit = this.form.querySelector('[data-quick-submit]');
    this.tabs = root.querySelector('[data-quick-tabs]');
    this.search = root.querySelector('[data-quick-search]');

    this.items = [];
    this.byVariant = new Map();
    this.quantities = new Map();
    this.group = '';
    this.term = '';
    this.page = 1;

    this.bind();
    this.load();
  }

  bind() {
    this.tabs?.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-quick-tab]');
      if (!tab) return;
      this.tabs.querySelectorAll('[data-quick-tab]').forEach((other) => {
        other.setAttribute('aria-selected', other === tab);
      });
      this.group = tab.dataset.quickTab;
      this.page = 1;
      this.render();
    });

    let timer;
    this.search?.addEventListener('input', (event) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        this.term = event.target.value.trim().toLowerCase();
        this.page = 1;
        this.render();
      }, 150);
    });

    this.prev?.addEventListener('click', () => this.goTo(this.page - 1));
    this.next?.addEventListener('click', () => this.goTo(this.page + 1));

    this.form.addEventListener('input', (event) => {
      const input = event.target.closest('[data-quick-quantity]');
      if (!input) return;
      const quantity = Math.max(0, Math.floor(Number(input.value) || 0));
      const id = Number(input.dataset.variant);
      if (quantity > 0) this.quantities.set(id, quantity);
      else this.quantities.delete(id);

      const cell = input.closest('tr')?.querySelector('[data-quick-line]');
      if (cell) cell.textContent = this.money(quantity * Number(input.dataset.price));
      this.recalculate();
    });

    // Vóór de lade in de header aan de beurt, want die luistert op het document.
    this.form.addEventListener('submit', () => this.fillHidden());
  }

  /* ---------- laden ---------- */

  async load() {
    const { source, section } = this.config;
    const url = (page) => `${source}?section_id=${encodeURIComponent(section)}&page=${page}`;

    try {
      const first = await this.fetchPage(url(1));
      const pages = first.pages || 0;
      const rest = [];
      for (let page = 2; page <= pages; page += 1) rest.push(url(page));

      let done = 1;
      const results = [first];
      this.setStatus(this.progress(done, pages));

      // Vier tegelijk: snel genoeg, zonder de winkel te bestoken.
      const queue = rest.slice();
      const worker = async () => {
        while (queue.length) {
          const next = queue.shift();
          results.push(await this.fetchPage(next));
          done += 1;
          this.setStatus(this.progress(done, pages));
        }
      };
      await Promise.all([worker(), worker(), worker(), worker()]);

      const seen = new Set();
      results.forEach((result) => {
        (result.items || []).forEach((item) => {
          if (seen.has(item.v)) return;
          seen.add(item.v);
          item.search = `${item.t} ${item.vt} ${item.s}`.toLowerCase();
          this.items.push(item);
          this.byVariant.set(item.v, item);
        });
      });

      // Op artikelnummer; zonder artikelnummer achteraan, dan op naam.
      this.items.sort((a, b) => {
        if (!a.s && b.s) return 1;
        if (a.s && !b.s) return -1;
        return QUICK_COLLATOR.compare(a.s, b.s) || QUICK_COLLATOR.compare(a.t, b.t);
      });

      this.setStatus('');
      this.render();
    } catch (error) {
      console.error('Besteltabel laden mislukt:', error);
      this.setStatus(this.config.text.error);
    }
  }

  async fetchPage(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`${response.status} bij ${url}`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = html.querySelector('[data-quick-data]');
    return data ? JSON.parse(data.textContent) : { pages: 0, items: [] };
  }

  progress(done, pages) {
    return pages > 1 ? `${this.config.text.loading} (${Math.round((done / pages) * 100)}%)` : this.config.text.loading;
  }

  setStatus(text) {
    if (!this.status) return;
    this.status.textContent = text;
    this.status.hidden = text === '';
  }

  /* ---------- tonen ---------- */

  get visibleItems() {
    return this.items.filter((item) => {
      const inGroup = this.group === '' || item.c.includes(this.group);
      const inSearch = this.term === '' || item.search.includes(this.term);
      return inGroup && inSearch;
    });
  }

  goTo(page) {
    this.page = page;
    this.render();
    this.root.querySelector('.quick__bar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  render() {
    const list = this.visibleItems;
    const per = this.config.perPage || 50;
    const pages = Math.max(1, Math.ceil(list.length / per));
    this.page = Math.min(Math.max(1, this.page), pages);
    const slice = list.slice((this.page - 1) * per, this.page * per);

    const fragment = document.createDocumentFragment();
    slice.forEach((item) => fragment.append(this.row(item)));
    this.body.replaceChildren(fragment);

    if (this.none) this.none.hidden = list.length > 0;
    if (this.pager) {
      this.pager.hidden = pages <= 1;
      this.pageInfo.textContent = this.config.text.page
        .replace('[page]', this.page)
        .replace('[pages]', pages)
        .replace('[count]', list.length);
      this.prev.disabled = this.page <= 1;
      this.next.disabled = this.page >= pages;
    }
    this.recalculate();
  }

  row(item) {
    const { text } = this.config;
    const tr = document.createElement('tr');
    tr.className = 'quick__row';

    const cell = (className, label) => {
      const td = document.createElement('td');
      if (className) td.className = className;
      if (label) td.dataset.label = label;
      tr.append(td);
      return td;
    };

    if (this.config.showImage) {
      const td = cell('quick__col-image');
      if (item.i) {
        const img = document.createElement('img');
        img.className = 'quick__thumb';
        img.src = item.i;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 48;
        img.height = 48;
        td.append(img);
      }
    }

    const product = cell('quick__col-product', text.product);
    const link = document.createElement('a');
    link.className = 'quick__title';
    link.href = item.u;
    link.dataset.quickView = item.h;
    link.textContent = item.t;
    product.append(link);
    if (item.vt) {
      const variant = document.createElement('span');
      variant.className = 'quick__variant';
      variant.textContent = item.vt;
      product.append(variant);
    }

    if (this.config.showSku) cell('quick__sku', text.sku).textContent = item.s || '-';

    if (this.config.retailDivisor) {
      const retail = document.createElement('s');
      retail.className = 'quick__retail';
      retail.textContent = this.money((item.p * 100) / this.config.retailDivisor);
      cell('quick__col-price', text.retail).append(retail);
    }

    cell('quick__col-price', text.price).textContent = this.money(item.p);

    const quantityCell = cell('quick__col-quantity', text.quantity);
    const quantity = this.quantities.get(item.v) || 0;
    if (item.a) {
      const id = `qty-${item.v}`;
      const label = document.createElement('label');
      label.className = 'visually-hidden';
      label.htmlFor = id;
      label.textContent = `${text.quantity}: ${item.t}`;
      const input = document.createElement('input');
      Object.assign(input, { className: 'quick__quantity', type: 'number', id, min: 0, step: 1, value: quantity });
      input.inputMode = 'numeric';
      input.dataset.quickQuantity = '';
      input.dataset.variant = item.v;
      input.dataset.price = item.p;
      quantityCell.append(label, input);
    } else {
      const soldOut = document.createElement('span');
      soldOut.className = 'quick__sold-out';
      soldOut.textContent = text.soldOut;
      quantityCell.append(soldOut);
    }

    const line = document.createElement('span');
    line.dataset.quickLine = '';
    line.textContent = this.money(quantity * item.p);
    cell('quick__col-total', text.total).append(line);

    return tr;
  }

  /* ---------- totaal en verzenden ---------- */

  recalculate() {
    let total = 0;
    this.quantities.forEach((quantity, id) => {
      const item = this.byVariant.get(id);
      if (item) total += quantity * item.p;
    });
    if (this.totalEl) this.totalEl.textContent = this.money(total);
    if (this.submit) this.submit.disabled = total === 0;
  }

  fillHidden() {
    this.hidden.replaceChildren();
    let index = 0;
    this.quantities.forEach((quantity, id) => {
      if (!this.byVariant.get(id)?.a) return;
      [['id', id], ['quantity', quantity]].forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `items[${index}][${key}]`;
        input.value = value;
        this.hidden.append(input);
      });
      index += 1;
    });
  }

  /*
    Bedragen staan in Shopify in centen. Het opmaakpatroon komt uit de
    winkelinstellingen, zodat een andere valuta of scheidingsteken vanzelf
    goed gaat.
  */
  money(cents) {
    const amount = (cents / 100).toFixed(2);
    const withComma = amount.replace('.', ',');

    const formatted = this.config.format
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', withComma.split(',')[0])
      .replace('{{amount_no_decimals}}', amount.split('.')[0])
      .replace('{{amount}}', amount);

    // Ronde bedragen zonder decimalen, net als `money_without_trailing_zeros`
    // in Liquid. Anders staat er in de ene rij €40 en in de andere €40,00.
    return formatted.replace(/[.,]00$/, '');
  }
}

document.querySelectorAll('.quick').forEach((root) => new QuickOrder(root));

/*
  Snel kijken zonder de tabel te verlaten. De inhoud wordt als losse sectie
  opgehaald, dus wat je in het venster ziet komt uit dezelfde Liquid als de rest
  van de site — geen tweede opmaak die na een maand uit de pas loopt.

  De titel blijft een gewone link. Zonder dit script, met het middelste
  muisknopje of via "openen in nieuw tabblad" ga je gewoon naar het product.
*/
class QuickView {
  constructor(dialog) {
    this.dialog = dialog;
    this.content = dialog.querySelector('[data-quick-view-content]');
    this.root = dialog.closest('.quick');
    if (!this.content || !this.root) return;

    this.root.addEventListener('click', (event) => this.onClick(event));

    dialog.querySelector('[data-quick-view-close]')?.addEventListener('click', () => dialog.close());

    // Klikken naast het venster sluit het. Het doel is dan de dialoog zelf,
    // want de inhoud vangt haar eigen kliks op.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }

  onClick(event) {
    const link = event.target.closest('[data-quick-view]');
    if (!link) return;

    // Nieuw tabblad, middelste knop of een toets erbij: laat de browser zijn
    // gang gaan. Alleen een gewone klik houden we tegen.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    this.open(link.dataset.quickView);
  }

  async open(handle) {
    this.content.textContent = '';
    this.dialog.showModal();

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${handle}?section_id=quick-view`);
      if (!response.ok) throw new Error(String(response.status));

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const body = html.querySelector('.quick-view__body');
      if (body) this.content.append(body);
    } catch (error) {
      // Lukt het niet, dan alsnog naar de productpagina: beter dan een leeg
      // venster waar niets in komt.
      console.error('Snel kijken mislukt:', error);
      this.dialog.close();
      window.location.href = `${window.Shopify?.routes?.root || '/'}products/${handle}`;
    }
  }
}

document.querySelectorAll('[data-quick-view-dialog]').forEach((dialog) => new QuickView(dialog));
